import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Klientsky formular pres magic link (krok KP1) - typovany klient nad
 * `/v1/client-invitations` (viz `packages/contexts/documents/src/public/plugin.ts`).
 *
 * Stejny vzor jako `contracts-api.ts` (T3): request body typy se ODVOZUJI
 * z generovaneho OpenAPI kontraktu, DTO se pisou rucne.
 *
 * Verejne cesty `/v1/public/client-intake/*` tady NEJSOU zamerne - web-admin
 * je nema proc znat (formular vyplnuje klient ve `apps/web-public`).
 */

export type ClientRole =
  | 'prodavajici'
  | 'pronajimatel'
  | 'spoluvlastnik'
  | 'zajemce-kupujici'
  | 'najemce'
  | 'zastupce-firmy';

export type ClientPartyKey = 'prodavajici' | 'kupujici' | 'pronajimatel' | 'najemce';
export type ClientChannel = 'email' | 'sms' | 'odkaz';
export type ClientInvitationStatus =
  | 'odeslano'
  | 'otevreno'
  | 'rozpracovano'
  | 'odeslano-klientem'
  | 'zkontrolovano-maklerem'
  | 'zneplatneno';

export type LvMismatchCode =
  'jmeno-neni-na-lv' | 'podil-neodpovida-lv' | 'podil-neuveden' | 'lv-bez-vlastniku';

export interface LvFindingDto {
  readonly code: LvMismatchCode;
  readonly closestLvOwner: string | null;
}

export interface SubmissionIssueDto {
  readonly field: string;
  readonly code: string;
}

export interface ClientSubmissionSummaryDto {
  readonly id: string;
  readonly status: 'rozpracovano' | 'odeslano' | 'prevzato';
  readonly identityMethod: 'mrz' | 'photo' | 'manual' | null;
  readonly consentGivenAt: string | null;
  readonly documentCopyConsent: boolean;
  /** Krok ISIR1b: souhlas s pouzitim rodneho cisla k lustraci v ISIR. */
  readonly birthNumberConsent: boolean;
  readonly lvFindings: readonly LvFindingDto[];
  readonly submittedAt: string | null;
  readonly displayName: string;
  readonly email: string;
  readonly phone: string;
  readonly address: string;
  readonly share: string | null;
  readonly maritalStatus: string | null;
  /** Rodne cislo a cislo dokladu chodi VYHRADNE maskovane (bod 6). */
  readonly birthNumberMasked: string | null;
  readonly documentNumberMasked: string | null;
  /** `true` = doklad uz neplati. Upozorneni maklerovi, ne chyba formulare. */
  readonly documentExpired: boolean;
  readonly issues: readonly SubmissionIssueDto[];
  /** Krok KP2: dotaznik k nemovitosti a rozpory s LV / SVJ / zakazkou. */
  readonly property: ClientIntakePropertyDto;
  readonly propertyFindings: readonly PropertyFindingDto[];
}

// ------------------------------------------------------------------ krok KP2

export type PropertySection =
  'nemovitost' | 'zavazky' | 'svj' | 'dokumenty' | 'pronajem' | 'financovani' | 'najemce';

export type PropertyFindingCode =
  | 'parcela-navic-proti-lv'
  | 'parcela-chybi-proti-lv'
  | 'jednotka-neodpovida-lv'
  | 'podil-jednotky-neodpovida-lv'
  | 'spoluvlastnik-neni-na-lv'
  | 'lv-nepotvrzeno-klientem'
  | 'hypoteka-bez-zastavy-na-lv'
  | 'zastava-na-lv-bez-hypoteky'
  | 'svj-uver-neuveden'
  | 'svj-fond-oprav-neodpovida-zaverce'
  | 'svj-zaverka-zastarala'
  | 'svj-bez-zaverky'
  | 'plocha-neodpovida-zakazce'
  | 'dispozice-neodpovida-zakazce'
  | 'najemne-neodpovida-zakazce'
  | 'cena-neodpovida-zakazce';

export interface PropertyFindingDto {
  readonly code: PropertyFindingCode;
  readonly severity: 'rozpor' | 'upozorneni';
  readonly detail: string | null;
}

export type IntakeDocumentKind =
  | 'nabyvaci-titul'
  | 'penb'
  | 'kolaudace'
  | 'stavebni-povoleni'
  | 'pudorys'
  | 'revize-plyn'
  | 'revize-elektro'
  | 'revize-komin'
  | 'prohlaseni-vlastnika'
  | 'najemni-smlouva'
  | 'fotka'
  | 'jine';

export type IntakeDocumentStatus =
  'ceka-na-nahrani' | 'nahrava-se' | 'kontrola-viru' | 'ulozeno' | 'zamitnuto';

export interface ClientIntakeDocumentDto {
  readonly id: string;
  readonly kind: IntakeDocumentKind;
  readonly status: IntakeDocumentStatus;
  readonly originalName: string | null;
  readonly fileId: string | null;
  readonly extracted: Readonly<Record<string, string | number>>;
  readonly extractionWarnings: readonly string[];
  readonly uploadedAt: string | null;
  readonly createdAt: string;
}

export interface MissingDocumentDto {
  readonly kind: string;
  readonly required: boolean;
}

/**
 * Dotaznik k nemovitosti tak, jak ho vraci API. Tvar je hluboky a UI z nej
 * ukazuje jen shrnuti - typ je proto zamerne volny (`unknown` uvnitr sekci),
 * aby se admin nemusel menit pri kazdem novem poli dotazniku.
 */
export interface ClientIntakePropertyDto {
  readonly facts: {
    readonly lvConfirmed: boolean | null;
    readonly layout: string | null;
    readonly floorAreaM2: number | null;
    readonly floor: number | null;
    readonly noKnownDefects: boolean | null;
    readonly defects: readonly {
      readonly description: string;
      readonly sinceYear: number | null;
    }[];
    readonly coOwners: readonly { readonly fullName: string; readonly share: string | null }[];
  };
  readonly obligations: {
    readonly mortgage: {
      readonly present: boolean | null;
      readonly bank: string | null;
      readonly approxBalanceCzk: number | null;
    };
    readonly executionPresent: boolean | null;
    readonly tenantsPresent: boolean | null;
    readonly disputesPresent: boolean | null;
  };
  readonly hoa: {
    readonly kind: string | null;
    readonly managerName: string | null;
    readonly managerContact: string | null;
    readonly monthlyAdvanceCzk: number | null;
    readonly debtToHoaCzk: number | null;
  };
  readonly rental: {
    readonly monthlyRentCzk: number | null;
    readonly depositCzk: number | null;
    readonly availableFrom: string | null;
  };
  readonly buyer: { readonly financing: string | null; readonly bank: string | null };
  readonly tenant: { readonly occupants: number | null; readonly moveInFrom: string | null };
}

export interface IntakeDocumentsViewDto {
  readonly documents: readonly ClientIntakeDocumentDto[];
  readonly missing: readonly MissingDocumentDto[];
}

export interface ProcessDocumentsResultDto {
  /** Kolik listin se zalozilo jako dokument u zakazky. */
  readonly attached: number;
  readonly extracted: number;
  readonly notExtracted: number;
  readonly documents: readonly ClientIntakeDocumentDto[];
}

export interface ClientInvitationDto {
  readonly id: string;
  readonly contractId: string | null;
  readonly caseId: string | null;
  readonly role: ClientRole;
  readonly partyKey: ClientPartyKey;
  readonly partyIndex: number;
  readonly channel: ClientChannel;
  readonly recipientName: string;
  readonly recipientEmail: string | null;
  readonly recipientPhone: string | null;
  readonly requireSms: boolean;
  readonly smsVerified: boolean;
  readonly expiresAt: string;
  readonly status: ClientInvitationStatus;
  readonly sentAt: string | null;
  readonly openedAt: string | null;
  readonly submittedAt: string | null;
  readonly reviewedAt: string | null;
  readonly remindedAt: string | null;
  readonly revokedAt: string | null;
  readonly spouseOfInvitationId: string | null;
  readonly createdAt: string;
  readonly submission: ClientSubmissionSummaryDto | null;
  /** Krok KP2: sekce dotazniku, dokumenty a soupis chybejicich (= ukoly). */
  readonly propertySections: readonly PropertySection[];
  readonly documents: readonly ClientIntakeDocumentDto[];
  readonly missingDocuments: readonly MissingDocumentDto[];
}

export interface ClientInvitationEventDto {
  readonly kind: string;
  readonly at: string;
  readonly byBroker: boolean;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface ClientInvitationDetailDto extends ClientInvitationDto {
  readonly timeline: readonly ClientInvitationEventDto[];
}

/** Odkaz se vraci JEDINKRAT - v DB je jen hash tokenu, znovu ho precist nelze. */
export interface IssuedInvitationDto {
  readonly invitation: ClientInvitationDto;
  readonly link: string;
  readonly delivered: boolean;
  /**
   * Krok MAIL1 bod 3 — PROČ se neodeslalo (`odesilani-vypnute`,
   * `hodinovy-strop`, …). `null` = odesláno, nebo kanál „jen zkopírovat".
   */
  readonly notDeliveredReason?: string | null;
}

export interface ConfirmReceiptResultDto {
  readonly invitation: ClientInvitationDetailDto;
  readonly crmPerson: {
    readonly displayName: string;
    readonly email: string;
    readonly phone: string;
    readonly address: string;
    readonly companyIco: string | null;
    readonly companyName: string | null;
  };
  /** Krok PROP2 bod 2: co se stalo v adresari — `null` = nezapsalo se. */
  readonly crmWrite: {
    readonly partyId: string;
    readonly created: boolean;
    readonly addedContacts: readonly ('email' | 'phone' | 'address')[];
    readonly linkedToCase: boolean;
    readonly conflicts: readonly {
      readonly kind: 'name' | 'email' | 'phone' | 'address';
      readonly existing: string;
      readonly submitted: string;
    }[];
  } | null;
  readonly contractFieldsWritten: number | null;
}

type CreateInvitationBody =
  ApiPaths['/v1/client-invitations']['post']['requestBody']['content']['application/json'];
export type CreateClientInvitationBody = CreateInvitationBody;

export async function listClientInvitations(filter: {
  readonly contractId?: string;
  readonly caseId?: string;
}): Promise<ClientInvitationDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/client-invitations', { params: { query: filter } }),
  )) as ClientInvitationDto[];
}

export async function getClientInvitation(id: string): Promise<ClientInvitationDetailDto> {
  return (await unwrap(
    apiClient.GET('/v1/client-invitations/{id}', { params: { path: { id } } }),
  )) as ClientInvitationDetailDto;
}

export async function createClientInvitation(
  body: CreateInvitationBody,
): Promise<IssuedInvitationDto> {
  return (await unwrap(apiClient.POST('/v1/client-invitations', { body }))) as IssuedInvitationDto;
}

type RemindBody =
  ApiPaths['/v1/client-invitations/{id}/reminder']['post']['requestBody']['content']['application/json'];

export async function remindClientInvitation(
  id: string,
  body: RemindBody,
): Promise<IssuedInvitationDto> {
  return (await unwrap(
    apiClient.POST('/v1/client-invitations/{id}/reminder', {
      params: { path: { id } },
      body,
    }),
  )) as IssuedInvitationDto;
}

export async function revokeClientInvitation(
  id: string,
  reason: string,
): Promise<ClientInvitationDto> {
  return (await unwrap(
    apiClient.DELETE('/v1/client-invitations/{id}', {
      params: { path: { id } },
      body: { reason },
    }),
  )) as ClientInvitationDto;
}

export async function confirmClientReceipt(
  id: string,
  overwriteBrokerValues: boolean,
): Promise<ConfirmReceiptResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/client-invitations/{id}/confirm', {
      params: { path: { id } },
      body: { overwriteBrokerValues },
    }),
  )) as ConfirmReceiptResultDto;
}

/** Krok KP2: dokumenty od klienta a soupis toho, co jeste chybi. */
export async function listInvitationDocuments(id: string): Promise<IntakeDocumentsViewDto> {
  return (await unwrap(
    apiClient.GET('/v1/client-invitations/{id}/documents', { params: { path: { id } } }),
  )) as IntakeDocumentsViewDto;
}

/**
 * Krok KP2: zpracovani listin od klienta - stav z antiviru, zalozeni
 * dokumentu u zakazky a vytezeni LOKALNIM modelem. Spousti ho makler vedome:
 * volani modelu trva sekundy a nema viset na kazdem vypisu.
 */
export async function processInvitationDocuments(id: string): Promise<ProcessDocumentsResultDto> {
  return (await unwrap(
    apiClient.POST('/v1/client-invitations/{id}/documents/process', {
      params: { path: { id } },
      body: {},
    }),
  )) as ProcessDocumentsResultDto;
}

/** Krok KP2: prepocet rozporu proti LV / hospodareni SVJ / zakazce. */
export async function compareInvitationFacts(
  id: string,
  body: {
    readonly lv?: {
      readonly parcels: string[];
      readonly unitNumber: string | null;
      readonly unitShare: string | null;
      readonly ownerNames: string[];
      readonly restrictions: string[];
    };
    readonly caseFacts?: {
      readonly floorAreaM2: number | null;
      readonly layout: string | null;
      readonly monthlyRentCzk: number | null;
      readonly priceCzk: number | null;
    };
  },
): Promise<PropertyFindingDto[]> {
  return (await unwrap(
    apiClient.POST('/v1/client-invitations/{id}/compare-facts', {
      params: { path: { id } },
      body,
    }),
  )) as PropertyFindingDto[];
}

export async function compareInvitationWithLv(
  id: string,
  lvOwners: readonly { readonly name: string; readonly share: string | null }[],
): Promise<LvFindingDto[]> {
  return (await unwrap(
    apiClient.POST('/v1/client-invitations/{id}/compare-lv', {
      params: { path: { id } },
      body: { lvOwners: [...lvOwners] },
    }),
  )) as LvFindingDto[];
}
